import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/zod-resolver";
import { z } from "zod";
import { Building2, Check, ChevronsUpDown, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import {
  useListMyTenants,
  useSwitchTenant,
  useCreateTenant,
  useDiscoverVendors,
  getGetMeQueryKey,
  getListMyTenantsQueryKey,
  getGetCurrentTenantQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { VerifiedBadge } from "@/components/verified-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WorkspaceAccessRequestPanel } from "@/components/workspace-access-request";

const createSchema = z.object({
  name: z.string().min(2, "Company name is required"),
  website: z.string().url().optional().or(z.literal("")),
  ueiCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{12}$/, "UEI must be 12 alphanumeric characters"),
});
type CreateForm = z.infer<typeof createSchema>;

interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTenantDialog({ open, onOpenChange }: CreateTenantDialogProps) {
  const qc = useQueryClient();
  const [samQuery, setSamQuery] = useState("");
  const [samPreview, setSamPreview] = useState<{
    uei: string;
    legalBusinessName: string;
    city?: string | null;
    state?: string | null;
  } | null>(null);
  const [duplicateUei, setDuplicateUei] = useState<string | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", website: "", ueiCode: "" },
  });

  const createMut = useCreateTenant({
    mutation: {
      onSuccess: async () => {
        toast.success("Workspace created");
        qc.removeQueries();
        await qc.invalidateQueries({ queryKey: getListMyTenantsQueryKey() });
        await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
        await qc.invalidateQueries({ queryKey: getGetCurrentTenantQueryKey() });
        await qc.invalidateQueries();
        onOpenChange(false);
        form.reset();
        setSamPreview(null);
        setSamQuery("");
      },
      onError: (e) => {
        const error = e as { message?: string; status?: number };
        if (error.status === 409) {
          setDuplicateUei(form.getValues("ueiCode").trim().toUpperCase());
          return;
        }
        toast.error(error.message ?? "Could not create workspace");
      },
    },
  });

  const sam = useDiscoverVendors(
    { uei: samQuery, onSpn: "all" },
    {
      query: {
        enabled: samQuery.length === 12,
        queryKey: ["tenant-switcher-sam", samQuery],
      },
    },
  );

  const onLookup = () => {
    const uei = form.getValues("ueiCode")?.trim().toUpperCase() ?? "";
    if (uei.length !== 12) {
      toast.error("Enter a 12-character UEI to look up SAM.gov");
      return;
    }
    setSamQuery(uei);
  };

  if (
    sam.data &&
    samQuery &&
    (!samPreview || samPreview.uei.toUpperCase() !== samQuery.toUpperCase())
  ) {
    const match = sam.data.vendors.find((v) => v.uei.toUpperCase() === samQuery.toUpperCase());
    if (match) {
      setSamPreview({
        uei: match.uei,
        legalBusinessName: match.legalBusinessName,
        city: match.city ?? null,
        state: match.state ?? null,
      });
      if (!form.getValues("name")) form.setValue("name", match.legalBusinessName);
    }
  }

  const onCreate = (data: CreateForm) => {
    const uei = data.ueiCode.trim().toUpperCase();
    createMut.mutate({
      data: { name: data.name, website: data.website || null, ueiCode: uei },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) setDuplicateUei(null);
      }}
    >
      <DialogContent>
        <form onSubmit={form.handleSubmit(onCreate)}>
          <DialogHeader>
            <DialogTitle>Create business workspace</DialogTitle>
            <DialogDescription>
              Every SPN workspace represents a real business. Enter your SAM.gov UEI and we'll look
              up your company. You'll become the Workspace Admin and an SPN admin will verify the
              UEI.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="t-uei">
                SAM.gov UEI <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="t-uei"
                  placeholder="12-character UEI"
                  {...form.register("ueiCode")}
                  data-testid="input-create-uei"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={onLookup}
                  disabled={sam.isFetching}
                  data-testid="button-create-lookup"
                >
                  <Search className="mr-1 h-4 w-4" />
                  {sam.isFetching ? "Looking up…" : "Look up"}
                </Button>
              </div>
              {form.formState.errors.ueiCode && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.ueiCode.message}
                </p>
              )}
              {samPreview && (
                <div className="rounded-md border border-border bg-muted/40 p-2 text-xs">
                  <div className="font-medium">{samPreview.legalBusinessName}</div>
                  <div className="text-muted-foreground">
                    UEI {samPreview.uei}
                    {samPreview.city || samPreview.state
                      ? ` · ${[samPreview.city, samPreview.state].filter(Boolean).join(", ")}`
                      : ""}
                  </div>
                </div>
              )}
              {sam.data && samQuery && !samPreview && (
                <p className="text-xs text-muted-foreground">
                  No active SAM.gov record found for that UEI.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-name">Company name</Label>
              <Input id="t-name" {...form.register("name")} />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-web">Website (optional)</Label>
              <Input id="t-web" placeholder="https://example.com" {...form.register("website")} />
              {form.formState.errors.website && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.website.message}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMut.isPending}
              data-testid="button-create-workspace-submit"
            >
              {createMut.isPending ? "Creating…" : "Create workspace"}
            </Button>
          </DialogFooter>
        </form>
        {duplicateUei && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
            <p className="mb-3 text-sm">
              A workspace already uses this UEI, so a duplicate workspace cannot be created. If
              this is your company, ask its administrators to approve your access.
            </p>
            <WorkspaceAccessRequestPanel initialUeiCode={duplicateUei} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface TenantSwitcherProps {
  currentTenantName: string | null;
  currentTenantId: string | null;
}

export function TenantSwitcher({ currentTenantName, currentTenantId }: TenantSwitcherProps) {
  const qc = useQueryClient();
  const { data: tenants = [] } = useListMyTenants();
  const switchMut = useSwitchTenant({
    mutation: {
      onSuccess: () => {
        toast.success("Switched workspace");
        qc.removeQueries();
        qc.invalidateQueries();
      },
      onError: (e) => toast.error(e?.message ?? "Could not switch workspace"),
    },
  });

  const [open, setOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="min-w-0 gap-2 max-w-[150px] sm:max-w-[260px]">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{currentTenantName ?? "No workspace"}</span>
            <ChevronsUpDown className="ml-auto h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {tenants.map((t) => (
            <DropdownMenuItem
              key={t.tenantId}
              onClick={() => {
                if (t.tenantId !== currentTenantId) {
                  switchMut.mutate({ data: { tenantId: t.tenantId } });
                }
              }}
              className="justify-between"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{t.tenantName}</span>
                {t.verified && <VerifiedBadge verified showLabel={false} />}
              </span>
              {t.tenantId === currentTenantId && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          ))}
          {tenants.length > 0 && <DropdownMenuSeparator />}
          <DropdownMenuItem onClick={() => setOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create new workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateTenantDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
